// /src/pages/Monetize/ListMonetize.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { hasPermission } from "../../utils/permissionUtils";
import DataTable from "react-data-table-component";
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Modal,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MonetizeForm from "./MonetizeForm";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";

export default function ListMonetize({ refresh }) {
  const theme = useTheme();
  const { chatbotId: paramsChatbotId } = useParams();

  const [monetizeData, setMonetizeData] = useState([]);
  const [filteredMonetizeData, setFilteredMonetizeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [modalMode, setModalMode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentMonetize, setCurrentMonetize] = useState(null);
  const [triggerFetch, setTriggerFetch] = useState(false);

  // Permission states
  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Permission check
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("advertisements", "view");
        const addPerm = await hasPermission("advertisements", "add");
        const editPerm = await hasPermission("advertisements", "edit");
        const deletePerm = await hasPermission("advertisements", "delete");

        setCanView(viewPerm);
        setCanAdd(addPerm);
        setCanEdit(editPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  // Fetch Data
  const fetchMonetizeData = useCallback(async () => {
    try {
      setLoading(true);
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.get(`/get-monetizes`, { params });
      const responseData =
        typeof response.data.data === "string"
          ? JSON.parse(response.data.data)
          : response.data.data;
      const data = Array.isArray(responseData) ? responseData : [];
      setMonetizeData(data);
      setFilteredMonetizeData(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching advertisement data:", err);
      setError("Failed to fetch advertisement data.");
    } finally {
      setLoading(false);
    }
  }, [paramsChatbotId]);

  useEffect(() => {
    document.title = "Advertisements | KlonIT Web Application";
    if (canView && !checkingPermissions) {
      fetchMonetizeData();
    }
  }, [refresh, triggerFetch, canView, checkingPermissions, fetchMonetizeData]);

  // Search filter
  useEffect(() => {
    const lower = searchText.toLowerCase();
    const filtered = monetizeData.filter(
      (item) =>
        item.title?.toLowerCase().includes(lower) ||
        item.page_content?.toLowerCase().includes(lower) ||
        item.page_url?.toLowerCase().includes(lower) ||
        item.days?.toString().includes(lower) ||
        item.expired_date?.toLowerCase().includes(lower) ||
        item.upload_date?.toLowerCase().includes(lower)
    );
    setFilteredMonetizeData(filtered);
  }, [searchText, monetizeData]);

  // Delete record
  const handleDelete = (id, title) => {
    Swal.fire({
      title: "Are you sure?",
      html: `You are about to delete: <b>${title}</b>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
          await axiosInstance.delete(`/delete-monetize/${id}`, { params });
          setError({ message: `Advertisement "${title}" deleted successfully.`, type: "success" });
          setTriggerFetch((prev) => !prev);
        } catch (err) {
          console.error("Error deleting data:", err);
          setError({ message: "Failed to delete advertisement.", type: "error" });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Modal handling
  const openModal = (mode, data = null) => {
    setModalMode(mode);
    setCurrentMonetize(data);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setModalMode(null);
      setCurrentMonetize(null);
    }, 300);
  };

  const handleSaveSuccess = () => {
    setError({ message: "Advertisement saved successfully!", type: "success" });
    setTriggerFetch((prev) => !prev);
  };

  // DataTable columns
  const columns = [
  {
    name: "Title",
    selector: (row) => row.title,
    sortable: true,
    grow: 2,
    cell: (row) => (
      <Box>
        <Typography variant="body2"   color="textPrimary">{row.title}</Typography>
      </Box>
    ),
  },
  {
    name: "URL",
    selector: (row) => row.page_url,
    sortable: true,
    grow: 2,
    cell: (row) => (
      <Box>
        <Typography variant="body2" color="primary" >
          <a
            href={row.page_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            {row.page_url}
          </a>
        </Typography>
      </Box>
    ),
  },
  {
    name: "Featured",
    selector: (row) => (row.is_default ? "Yes" : "No"),
    sortable: true,
    cell: (row) => (
      <Box>
        <Typography variant="body2"   color="textPrimary">
          {row.is_default ? "Yes" : "No"}
        </Typography>
      </Box>
    ),
  },
  {
    name: "Days",
    selector: (row) => row.days,
    sortable: true,
    cell: (row) => (
      <Box>
        <Typography variant="body2"   color="textPrimary">{row.days}</Typography>
      </Box>
    ),
  },
  {
    name: "Expires",
    selector: (row) =>
      row.expired_date ? new Date(row.expired_date).toLocaleDateString() : "N/A",
    sortable: true,
    cell: (row) => (
      <Box>
        <Typography variant="body2"   color="textPrimary">
          {row.expired_date
            ? new Date(row.expired_date).toLocaleDateString()
            : "N/A"}
        </Typography>
      </Box>
    ),
  },
  {
    name: "Uploaded",
    selector: (row) =>
      row.upload_date ? new Date(row.upload_date).toLocaleDateString() : "N/A",
    sortable: true,
    cell: (row) => (
      <Box>
        <Typography variant="body2"  color="textPrimary">
          {row.upload_date
            ? new Date(row.upload_date).toLocaleDateString()
            : "N/A"}
        </Typography>
      </Box>
    ),
  },
  {
    name: "Actions",
    cell: (row) => (
      <Box display="flex" gap={1}>
        {canEdit && (
          <IconButton
            color="primary"
            size="small"
            onClick={() => openModal("edit", row)}
          >
            <EditIcon />
          </IconButton>
        )}
        {canDelete && (
          <IconButton
            color="error"
            size="small"
            onClick={() => handleDelete(row.id, row.title)}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    ),
    ignoreRowClick: true,
  },
];

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  // Permission handling UI
  if (checkingPermissions)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress size={30} />
        <Typography ml={2}>Checking permissions...</Typography>
      </Box>
    );

  if (!canView)
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error">
          <strong>Access Denied</strong>
          <br />
          You don't have permission to view advertisements.
        </Alert>
      </Box>
    );

  // Render UI
  return (
    <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px", bgcolor: "background.paper" }}>
      <Typography variant="h5" sx={{ mb: 3 }} color="textPrimary">
        Manage <strong>Advertisements</strong>
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="text.primary">
          Manage and monitor your ad placements
        </Typography>
        <Box display="flex" gap={2}>
          {canAdd && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => openModal("add")}
            >
              Add Advertisement
            </Button>
          )}
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search advertisements..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress size={30} />
          <Typography mt={2}>Loading advertisements...</Typography>
        </Box>
      ) : filteredMonetizeData.length === 0 ? (
        <Box textAlign="center" py={4}>
          <i className="fa fa-exclamation-triangle fa-3x text-muted" />
          <Typography mt={2} color="text.primary">
            No advertisement data available
          </Typography>
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={filteredMonetizeData}
          pagination
          responsive
          highlightOnHover
          pointerOnHover
          customStyles={customDataTableStyles}
        />
      )}

      {error && (
        <Alert
          severity={error.type === "error" ? "error" : "success"}
          sx={{ mt: 3 }}
        >
          {error.message}
        </Alert>
      )}

      {/* MUI Modal */}
      <Modal open={isModalOpen} onClose={closeModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "80%", md: "700px" },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="textPrimary">
              {modalMode === "edit" ? "Edit Advertisement" : "Add Advertisement"}
            </Typography>
            <IconButton onClick={closeModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          {modalMode && (
            <MonetizeForm
              mode={modalMode}
              chatbotId={paramsChatbotId}
              initialData={currentMonetize}
              onClose={closeModal}
              onSaveSuccess={handleSaveSuccess}
              permission={{ canAdd, canEdit }}
            />
          )}
        </Box>
      </Modal>
    </Paper>
  );
}
